<template>
    <v-container max-height="70vh">
        <v-card
        class="mx-auto"
        width="100%"
        >
        <v-toolbar
        color="secondary"
        dark
        >
            <v-toolbar-title v-if="showToolbarTitle">{{ pageTitle }}</v-toolbar-title>

            <v-spacer></v-spacer>
            
            <v-layout fill-height mt-3>
                <v-slide-x-reverse-transition>
                    <v-select v-if="showToolbarDropdown"
                    v-bind:items="this.caseCollectionItems"
                    v-model="currentCollectionSelected"
                    label="Select a collection"
                    dense
                    solo
                    light></v-select>
                </v-slide-x-reverse-transition>
            </v-layout>

            <v-btn
            v-if="pageTitle==='My Cases' && !searchOpen && smallScreen" 
            icon
            x-small
            @click="dropdownOpen=!dropdownOpen">
                <v-icon>mdi-form-dropdown</v-icon>
            </v-btn>

            <v-spacer></v-spacer>


            <v-layout fill-height mt-3>
                <v-slide-x-reverse-transition>
                    <v-text-field v-if="searchOpen"
                    @change="onSearchTermChange"
                    label="Search a treatment name"
                    dense
                    solo
                    light
                    transition="scroll-x-reverse-transition"></v-text-field>
                </v-slide-x-reverse-transition>
            </v-layout>



            <v-btn color="primary" 
            icon
            @click="searchOpen=!searchOpen">
            <v-icon>mdi-magnify</v-icon>
            </v-btn>
        </v-toolbar>

        <v-expansion-panels accordion>
                <v-expansion-panel>
                <v-expansion-panel-header disable-icon-rotate :v-slot:default="filterOpen">
                    <v-col cols="2">Filter</v-col>
                    <v-col
                    cols="10"
                    class="text--secondary"
                    >
                        <v-fade-transition leave-absolute>
                            <span
                            v-if="filterOpen"
                            key="0"
                            >
                            Select Filter Options
                            </span>
                            <span
                            v-else
                            key="1"
                            >
                                <span> Category: {{ getCurrentCategories }}</span>
                                <span> Sort By: {{ filterOptions.sortBy || 'Not Selected' }}</span>
                            </span>
                        </v-fade-transition>
                    </v-col>
                    <template v-slot:actions>
                        <v-icon color="primary">mdi-filter-menu</v-icon>
                    </template>                
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-row no-gutters>
                        <v-col cols="5">
                            <category-control
                            :parentSelectMode="false"
                            @valueChanged="categoryChanged($event)"></category-control>
                        </v-col>
                        <v-col cols="3">
                            <v-row>
                                <v-select
                                    v-model="filterOptions.sortBy"
                                    :items="sortOptions"   
                                    label="Sort By"
                                    flat
                                ></v-select>
                            </v-row>
                            <v-row>
                                <v-checkbox 
                                :input-value="false"
                                label="Favourites"
                                color="orange"
                                @change="onFavouritesChanged"></v-checkbox>
                            </v-row>
                        </v-col>
            
                        <v-divider
                        vertical
                        class="mx-4"
                        ></v-divider>
            
                        <v-col cols="3">
                        Refine your treatment search using the filtering options
                        <br>
                        <a href="javascript:void(0)">Learn more</a>
                        </v-col>
                    </v-row>
        
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                        text
                        color="secondary"
                        >
                            Close
                        </v-btn>
                        <v-btn
                        text
                        color="primary"
                        @click="resetFilterOptions()"
                        >
                            Reset
                        </v-btn>
                    </v-card-actions>
                </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

        <v-list 
        v-if="!loading"
        two-line subheader
        max-height="60vh"
        class="overflow-y-auto">
            <v-list-item
            v-for="item in shownCases"
            :key="item.id"
            >
            <v-list-item-action>
                <v-list-item-title v-text="item.data.upvotes"></v-list-item-title>
            </v-list-item-action>

            <v-list-item-content>
                <v-list-item-title v-text="item.data.title"></v-list-item-title>
                <v-list-item-subtitle v-text="`by ${item.data.ownerName} | ${item.data.dateAdded}`"></v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-content>
                <v-list-item-subtitle v-text="`${item.data.category}`"></v-list-item-subtitle>
            </v-list-item-content>

            <v-spacer></v-spacer>

            <v-icon v-if="!item.data.public" color="primary" class="pa-3">mdi-eye</v-icon>

            <v-list-item-action>
                <v-btn icon @click.stop="favouriteCase(item)">
                <v-icon :color="isCaseFavourite(item.id) ? 'yellow' : 'grey lighten-3'">mdi-star</v-icon>
                </v-btn>
            </v-list-item-action>
            <v-divider vertical></v-divider>

            <!-- Show this one if the user owns it (i.e. they can edit it) -->
            <v-list-item-action v-if="uid===item.data.owner">
                <v-btn icon @click.stop="editCase(item)">
                <v-icon color="grey lighten-1">mdi-pencil</v-icon>
                </v-btn>
            </v-list-item-action>
            <v-list-item-action v-if="uid===item.data.owner">
                <v-btn icon @click.stop="deleteCase(item)">
                <v-icon color="red lighten-1">mdi-delete</v-icon>
                </v-btn>
            </v-list-item-action>
            
            <v-list-item-action>
                <v-btn icon @click.stop="viewCase(item)">
                    <v-icon color="grey lighten-1">mdi-information</v-icon>
                </v-btn>
            </v-list-item-action>
            </v-list-item>
        </v-list>

        <v-progress-circular
        v-if="loading"
        color="primary"
        indeterminate
        :size="64"
        ></v-progress-circular>

        <v-toolbar light dense>
            <v-btn :disabled="currentPage===0" @click.stop="prevPage(localCaseSnapshots[0])" icon color="primary">
                <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-card-title>{{currentPage}}</v-card-title>
            <v-spacer></v-spacer>
            <v-btn :disabled="localCaseSnapshots.length < filterOptions.pageSize" @click.stop="nextPage(localCaseSnapshots[localCaseSnapshots.length - 1])" icon color="primary">
                <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
        </v-toolbar>
        </v-card>

        <!-- VIEW CASE DIALOG -->
        <v-dialog v-model="viewCaseDialog" :retain-focus="false" fullscreen hide-overlay persistent transition="dialog-bottom-transition">
            <view-case-screen-popup 
            v-if="viewCaseDialog"
            v-on:dialogClosed="closeViewCase()"
            :case="currentShownCase.data"
            :caseId="currentShownCase.id"
            :uid="uid"
            :showToolbar="true">
            </view-case-screen-popup>
        </v-dialog>

        <!-- EDIT CASE DIALOG -->
        <v-dialog v-model="editCaseDialog" scrollable persistent max-width="800px">
            <AddCasePopup
            v-on:dialogClosed="closeEditCase()"
            label="Edit Case"
            :editMode="true"
            :originalCase="currentShownCase"></AddCasePopup>
        </v-dialog>
    </v-container>
</template>

<script>
import ViewCaseScreenPopup from './../Popups/ViewCaseScreenPopup.vue'
import AddCasePopup from './../Popups/AddCasePopup.vue'
import TreatmentDataService from './../../../services/treatmentDataService.js';
import UserDataService from './../../../services/userDataService.js';
import CategoryControl from './../../Treatments/Category/CategoryControl.vue'
import Toast from './../../../plugins/customToast.js';
import swal from 'sweetalert2';

  export default {
    data () {
      return {
        filterOptions: {
            sortBy: "Most Popular",
            categories: [],
            searchTerm: null,
            pageSize: 4,
            isPublic: null,
            organisation: null,
            owner: null,
            favourites: {
                flag: false,
                items: []
            }
        },
        sortOptions: ['Newest', 'Recently Updated', 'Most Popular', 'A-Z Ascending', 'A-Z Descending'],
        caseCollectionItems: ['Personal', 'Organisation'],
        viewCaseDialog: false,
        currentShownCase: {},
        searchOpen: false,
        dropdownOpen: false,
        filterOpen: false,
        editCaseDialog: false,
        treatmentService: null,
        userService: null,
        localCaseSnapshots: [],
        currentPage: 0,
        loading: false,
      }
    },
    components: {
        ViewCaseScreenPopup,
        AddCasePopup,
        CategoryControl
    },
    props: 
    {
      pageTitle: String,
      uid: String,
      orgid: String,
      favourites: Array,
      currentCollectionSelected: String
    },
    methods: {
        closeViewCase() {
            this.viewCaseDialog = false;
        },
        closeEditCase() {
            this.editCaseDialog = false;
            //Reload the page to account for any updated cases
            this.firstPageLoad();
        },
        viewCase(item) {
            if (item.id != null){
            this.currentShownCase = item;
            console.log("Viewing Case Item: " + item.data.title + " by " + item.data.ownerName) // eslint-disable-line no-console
            this.viewCaseDialog = true;
            }
        },
        isCaseFavourite(id) {
            if (this.favourites) {
                if (this.favourites.length > 0) {
                    if (this.favourites.includes(id)){
                        return true;
                    }
                }
            }
            return false;
        },
        favouriteCase(item) {
            var that = this;
            if (item.id != null) {
                //Check if it has already been favourited
                if (!this.isCaseFavourite(item.id)) {
                    //call userDataService method to update the favourites list
                    this.userService.favourite(this.filterOptions.owner, item.id, true)
                    .then(() => {
                        //Checking if any favourites exist (otherwise we'd have to initialise)
                        if (!that.favourites) {
                            if (that.favourites.length < 1) {
                                that.favourites = [];
                            }
                        }

                        //Then push new favourite in for the local item
                        that.favourites.push(item.id);
                        //Update the filterOptions items favourites too
                        that.filterOptions.favourites.items = that.favourites;
                    })
                    .catch(error => {
                        //Output that the treatment couldn't be favourited (Maybe use a toast to do this)
                        console.log(error); //eslint-disable-line
                    });
                } else {
                    //call userDataService method to unfave
                    this.userService.unfavourite(this.filterOptions.owner, item.id, true)
                    .then(() => {
                        //Then refresh the user favourites list/prop
                        if (!that.favourites) {
                            if (!that.favourites.length) {
                                that.favourites = [];
                            }
                        } else {
                            //Remove the Item from the favourites list
                            var index = that.favourites.indexOf(item.id);
                            if (index !== -1) {
                                that.favourites.splice(index, 1);
                            }
                        }
                        //Update the filterOptions items favourites too
                        that.filterOptions.favourites.items = that.favourites;
                    })
                    .catch(error => {
                        //Output that the treatment couldn't be favourited (Maybe use a toast to do this)
                        console.log(error); //eslint-disable-line
                    });
                }
            }
        },
        editCase(item) {
            if (item.id != null) {
                this.currentShownCase = item;
                console.log("Editing Case Item: " + item.data.title + " by " + item.data.ownerName) // eslint-disable-line no-console
                this.editCaseDialog = true;
            }
        },
        deleteCase(item) {
          //Swal event in here
          swal.fire({
            title: 'Are you sure you wish to delete the case?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Confirm'
            }).then((result) => {
                if (result.value) {
                    if (item.id != null) {
                    var treatmentIds = item.data.treatments.map(t => t.id);
                    this.treatmentService.deleteCaseById(item.id, treatmentIds)
                    .then(function (){
                        swal.fire(
                        'Deleted!',
                        `Your ${item.data.title} case has been deleted.`,
                        'success'
                        );
                    }).catch(function(error){
                        swal.fire(
                        'Error when deleting treatment!',
                        `Your ${item.data.title} case could not be deleted: ${error}.`,
                        'error'
                        );
                    });
                    }
                }
            })
        },
        resetFilterOptions(){
            this.filterOptions = {
                sortBy: "Most Popular",
                categories: [],
                searchTerm: null,
                pageSize: 4,
                isPublic: null,
                organisation: null,
                owner: null
            }
            this.firstPageLoad();
        },
        onSearchTermChange(value){
            //If the search is greater than two characters, set it as the search term
            if (value.length > 2) {
                this.filterOptions.searchTerm = value;
            } else {
                this.filterOptions.searchTerm = null;
            }
        },
        onFavouritesChanged(value){
            if (value) {
                //Set favourites to the list propped in:
                this.filterOptions.favourites.flag = true;
                this.filterOptions.favourites.items = this.favourites;
            } else {
                this.filterOptions.favourites.flag = false;
                this.filterOptions.favourites = [];
            }
        },
        categoryChanged(categoriesSelected){
            this.filterOptions.categories = categoriesSelected;
        },
        firstPageLoad(){
            this.loading = true;

            this.treatmentService.firstPaginatedPage('cases', this.currentCollectionSelected, this.filterOptions)
            .then((cases) => {
                this.localCaseSnapshots = cases;
                //Reset the pagination counter
                this.currentPage = 0;
                this.loading = false;
            })
            .catch((error) => {
                Toast.fire({  icon: 'error',  title: `Error getting cases: ${error}!`});
                console.log(error); //eslint-disable-line
                this.loading = false;
            });
        },
        nextPage(last){
            if (last){
                this.treatmentService.nextPaginatedPage('cases', this.currentCollectionSelected, this.filterOptions, last)
                .then((cases) => {
                    this.localCaseSnapshots = cases;
                    this.currentPage += 1;
                })
                .catch((error) => {
                    Toast.fire({  icon: 'error',  title: `Error getting cases: ${error}!`});
                    console.log(error); //eslint-disable-line
                });
            } else {
                this.firstPageLoad();
            }
        },
        prevPage(first){
            if (first) {
                this.treatmentService.prevPaginatedPage('cases', this.currentCollectionSelected, this.filterOptions, first)
                .then((cases) => {
                    this.localCaseSnapshots = cases;
                    this.currentPage -= 1;
                })
                .catch((error) => {
                    Toast.fire({  icon: 'error',  title: `Error getting cases: ${error}!`});
                    console.log(error); //eslint-disable-line
                });
            } else {
                this.firstPageLoad();
            }
        },
    },
    mounted(){
        //Initialise a new treatmentDataService
        this.treatmentService = new TreatmentDataService(this.$route.params.userDb);
        this.userService = new UserDataService();
        this.filterOptions.owner = this.$route.params.userDb.uid;
        this.filterOptions.organisation = this.$route.params.userDb.orgid;

        //Do the first page load
        this.firstPageLoad();
    },
    watch: {
        filterOptions : {
            // the callback won't be called immediately after the start of the observation
            immediate: false, 
            deep: true,
            handler (val) {
                console.log(val); //eslint-disable-line
                //If our filter options change, repeat the firstPageLoad
                this.firstPageLoad();
            }
        }
    },
    computed: {
        shownCases(){
            var caseItems = [];

            //Formatting the case snapshots into a presentable/showable caseItem format 
            if(this.localCaseSnapshots.length > 0) {
                this.localCaseSnapshots.forEach(doc => {
                    caseItems.push({id: doc.id, data: doc.data()});
                });
            }
            return caseItems;
        },
        getCurrentCategories(){
            var categories = this.filterOptions.categories;

            if (categories.length > 0){
                var categoryString;
                categories.forEach(function (category){
                    if (categoryString) {
                        categoryString = categoryString  + ", " + category;
                    } else {
                        categoryString = category;
                    }                    
                })
                return categoryString;
            } else {
                return 'Not Selected';
            }
        },
        smallScreen(){
            if (this.$vuetify.breakpoint.name === "xs"){
                return true;
            }
            return false;
        },
        showToolbarTitle(){
            if (this.smallScreen){
                return !this.searchOpen && !this.dropdownOpen;
            }
            return true;
        },
        showToolbarDropdown(){
            if (this.pageTitle!=="My Cases") {
                return false;
            }

            if (this.smallScreen){
                return !this.searchOpen && this.dropdownOpen;
            }
            return true;
        }
    }
  }
</script>